import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Logo from '../components/Logo';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [fields, setFields] = useState({ newPassword: '', confirm: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'done'>('idle');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (fields.newPassword !== fields.confirm) {
      setError('Passwords do not match');
      return;
    }
    if (fields.newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setStatus('loading');
    try {
      const res = await fetch('https://auth.ts1cloud.com/auth/password-reset/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword: fields.newPassword }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((data as any).error || 'Reset failed');
      setStatus('done');
    } catch (err: any) {
      setError(err.message || 'Something went wrong');
      setStatus('idle');
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-pattern px-4">
        <div className="bg-app-panel/80 backdrop-blur-sm p-8 rounded-lg shadow-lg max-w-md w-full text-center space-y-4">
          <p className="text-red-600 font-medium">Invalid reset link.</p>
          <Link to="/forgot-password" className="text-indigo-600 hover:text-indigo-500 text-sm">
            Request a new one
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-pattern py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="bg-app-panel/80 backdrop-blur-sm p-8 rounded-lg shadow-lg">
          <div className="flex justify-center">
            <Logo className="h-12 w-auto mb-6 text-indigo-600" />
          </div>
          <h2 className="text-center text-2xl font-bold text-gray-900">Set a new password</h2>
          {status === 'done' ? (
            <div className="mt-6 text-center space-y-4">
              <p className="text-gray-700">Your password has been reset.</p>
              <Link to="/login" className="block text-indigo-600 hover:text-indigo-500 text-sm font-medium">
                Sign in now
              </Link>
            </div>
          ) : (
            <>
              {error && (
                <div className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>
              )}
              <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
                <input
                  type="password"
                  required
                  placeholder="New password"
                  autoComplete="new-password"
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  value={fields.newPassword}
                  onChange={(e) => setFields({ ...fields, newPassword: e.target.value })}
                />
                <input
                  type="password"
                  required
                  placeholder="Confirm new password"
                  autoComplete="new-password"
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  value={fields.confirm}
                  onChange={(e) => setFields({ ...fields, confirm: e.target.value })}
                />
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  {status === 'loading' ? 'Resetting...' : 'Reset password'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
